import { createFileRoute, Outlet, useMatches } from '@tanstack/react-router';
import clsx from 'clsx';

import { router } from '@/app/lib/tanstack-router';
import { CheckIcon } from '@/icons/fa/regular';
import { ContentSection } from '@/shared/components/content-section';
import { Sidebar } from '@/shared/components/sidebar';

type OnboardingPath = Extract<keyof typeof router.routesByPath, `/memberships/setup${string}`>;

const onboardingSteps: Array<{ name: string; paths: OnboardingPath[] }> = [
  { name: 'Link a Membership', paths: ['/memberships/setup'] },
  { name: 'Account Nicknames', paths: ['/memberships/setup/$member-number/aliases'] },
  { name: 'Notifications', paths: ['/memberships/setup/$member-number/notifications'] },
  { name: 'TAP Enrollment', paths: ['/memberships/setup/$member-number/tap'] },
  { name: 'Newsletter', paths: ['/memberships/setup/$member-number/newsletter'] },
  { name: 'Go Paperless', paths: ['/memberships/setup/$member-number/go-paperless'] },
  {
    name: 'Automatic Payments',
    paths: [
      '/memberships/setup/$member-number/automatic-payments',
      '/memberships/setup/$member-number/automatic-payments/setup'
    ]
  },
  { name: 'Complete', paths: ['/memberships/setup/$member-number/complete'] }
];

export const Route = createFileRoute('/memberships/setup')({
  component: SetupLayout,
  beforeLoad: async ({ context }) => ({
    user: await context.ensureUser()
  })
});

function SetupLayout() {
  const matches = useMatches();

  const currentRoute = matches[matches.length - 1]?.routeId?.replace(/\/$/, '');
  const currentStepIndex = onboardingSteps.findIndex(
    step => step.paths.some(path => path === currentRoute));

  return (
    <main className="flex flex-col md:flex-row h-full w-full p-0">
      <Sidebar>
        <ol role="list" className="hidden md:block overflow-hidden">
          {onboardingSteps.map((step, stepIndex) => (
            <li
              key={step.name}
              className={clsx(
                'relative group [&:not(:last-child)]:pb-10',
                '[&:not(:last-child)]:before:absolute [&:not(:last-child)]:before:left-4 [&:not(:last-child)]:before:top-9 [&:not(:last-child)]:before:-bottom-4 [&:not(:last-child)]:before:w-0.5',
                stepIndex < currentStepIndex ?
                  '[&:not(:last-child)]:before:bg-primary-800' :
                  '[&:not(:last-child)]:before:bg-gray-300'
              )}
            >
              <div className="relative flex items-center" aria-current={stepIndex === currentStepIndex ? 'step' : undefined}>
                <span aria-hidden="true" className="flex h-9 items-center">
                  <span
                    className={clsx(
                      'relative z-10 flex size-8 items-center justify-center rounded-full transition-colors',
                      stepIndex < currentStepIndex && 'bg-primary-800',
                      stepIndex === currentStepIndex && 'border-2 border-primary-800 bg-white',
                      stepIndex > currentStepIndex && 'border-2 border-gray-300 bg-white'
                    )}
                  >
                    {stepIndex < currentStepIndex ? (
                      <CheckIcon aria-hidden="true" className="size-5 text-white" />
                    ) : (
                      <span
                        className={clsx(
                          'size-2.5 rounded-full transition-colors',
                          stepIndex === currentStepIndex && 'bg-primary-800',
                          stepIndex > currentStepIndex && 'bg-transparent'
                        )}
                      />
                    )}
                  </span>
                </span>

                <span className="ml-4 flex min-w-0 flex-col">
                  <span
                    className={clsx(
                      'text-sm font-medium',
                      stepIndex < currentStepIndex && 'text-gray-900',
                      stepIndex === currentStepIndex && 'text-primary-800',
                      stepIndex > currentStepIndex && 'text-gray-500'
                    )}
                  >
                    {step.name}
                  </span>
                </span>
              </div>
            </li>
          ))}
        </ol>
      </Sidebar>
      <ContentSection bg="white">
        <div className="w-full">
          <Outlet />
        </div>
      </ContentSection>
    </main>
  );
}
